import {Injectable} from '@angular/core';
import {WebmateOptions} from '../../../webmate-options';
import {Observable} from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {AuthInterceptorSkipHeader} from '../../constants/interceptor-custom-headers.constants';
import {UserId} from '@webmate-web-core/shared/types/ids';
import {map} from "rxjs/operators";
import {AuthToken} from '@webmate-web-core/shared/services/auth.service';
import {GenericApiResponse} from "@webmate-web-core/shared/api/apibase.api";

@Injectable({
    providedIn: 'root'
})
export class AuthApi {

    constructor(private httpClient: HttpClient, private webmateOptions: WebmateOptions) {
    }

    getUnauthorizedHeader(): HttpHeaders {
        // All calls in this API are obviously unauthorized. Use this header to skip them in the interceptor
        return new HttpHeaders().set(AuthInterceptorSkipHeader, "");
    }

    getAuthToken(username: string, password: string): Observable<AuthToken> {
        let postBodyArgs = `username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}&client_id=webmate-web-ui&client_secret=webmate-web-ui&grant_type=password`;
        return this.httpClient.post<AuthToken>('/auth/token', postBodyArgs, {headers: this.getUnauthorizedHeader().set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')});
    }

    refreshAuthToken(refreshToken: string): Observable<AuthToken> {
        let postBodyArgs = `refresh_token=${refreshToken}&client_id=webmate-web-ui&client_secret=webmate-web-ui&grant_type=refresh_token`;
        return this.httpClient.post<AuthToken>('/auth/token', postBodyArgs, {headers: this.getUnauthorizedHeader().set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')});
    }

    /**
     * This method is used to check if the user is authenticated. It will trigger token refreshing if necessary (by means of auth interceptor).
     * Should not be used in general.
     */
    probeAuthentication(): Observable<void> {
        return this.httpClient.get('/auth/probe', {}).pipe(map(_=> undefined));
    }

    activateUserAccount(token: string): Observable<any> {
        return this.httpClient.post('/users/activate/' + token, {}, {headers: this.getUnauthorizedHeader()});
    }

    requestResetPassword(email: string, url: string): Observable<any> {
        let postBodyArgs = {
            email: email,
            ResetPasswordLinkForBackend: url
        };
        return this.httpClient.post('/users/reset-request', postBodyArgs, {headers: this.getUnauthorizedHeader().set('Content-Type', 'application/json')});
    }

    resetPassword(password: string, token: string): Observable<any> {
        let postBodyArgs = {
            password: password,
        };
        let headers = new HttpHeaders();
        headers.append('Content-Type', 'application/json');
        return this.httpClient.post('/users/reset-password/' + token, postBodyArgs, {headers: this.getUnauthorizedHeader().set('Content-Type', 'application/json')});
    }

    applyChangeEmailToken(token: string): Observable<GenericApiResponse> {
        return this.httpClient.post<GenericApiResponse>('/users/change-email/' + token, {}, {headers: this.getUnauthorizedHeader()});
    }

    impersonateUser(userId: UserId, reason: string): Observable<AuthToken> {
        let o = {
            target: userId,
            reason: reason
        };
        return this.httpClient.post<AuthToken>("/auth/impersonate", o);
    }

    registerCustomer(email: string, username: string, organization: string, metaData: object): Observable<any> {
        let postBodyArgs = {
            userEmail: email,
            userName: username,
            customerName: organization,
            registrationInfo: metaData
        };
        let headers = new HttpHeaders();
        headers.append('Content-Type', 'application/json');
        return this.httpClient.post('/customer/register', postBodyArgs, {headers: this.getUnauthorizedHeader().set('Content-Type', 'application/json')});
    }

    getAuthRealmForDomain(domain: string): Observable<any> {
        return this.httpClient.get('/auth/oidc/realm?domain='+domain);
    }

}
